import {
  FILTERED,
  FluidRowData,
  ID,
  SEARCHED,
  VISIBLE,
} from '@lmig/fluid-core';

import { Utils } from '../../../../utils/utils';
import { sendEvent } from '../../../fluid-data-layer/fluid-data-layer.utils';
import { FluidTable } from '../../fluid-table';
import { FluidTableHelper } from '../../helpers/fluid-table-helper.interface';
import { processTableFilters } from '../../internal/filter/fluid-table-filter.utils';
import { searchDataSource } from '../../internal/fluid-table-search.utils';

export class FluidTableSearchHelper implements FluidTableHelper {
  /**
   * Holds a reference to the current table
   */
  host: FluidTable;

  /**
   * Whether a search is currently applied or no
   * @private
   */
  private searchApplied: boolean;

  /**
   * The current search term held in the search box
   * @private
   */
  private searchTerm: string;

  /**
   * A memoized record of previous searches so that we can do a memoized
   * lookup for repeated searches in the same dataset
   * @private
   */
  private memoizedSearch: Record<string, FluidRowData[]> = {};

  /**
   * Whether the most recent search produced an empty set or not
   * @private
   */
  private searchHasResults: boolean;

  /**
   * Creates a new instance with the current table host
   * @param tableInstance
   * @param searchTerm
   */
  constructor(tableInstance: FluidTable, searchTerm?: string) {
    this.host = tableInstance;
    if (searchTerm) {
      this.setSearchTerm(searchTerm);
      this.searchTable();
    }
  }

  /**
   * Sets the 'searchApplied' state - used to determine whether to apply search
   * when updating meta-data (avoids unneccessary search when no search is actually
   * applied)
   * @param searchApplied
   */
  setSearchApplied(searchApplied: boolean): void {
    this.searchApplied = searchApplied;
  }

  /**
   * Returns the value of the search results flag
   */
  hasSearchResults() {
    return this.searchHasResults;
  }

  /**
   * Returns true if a search is currently applied
   */
  isSearchApplied(): boolean {
    return this.searchApplied;
  }

  /**
   * Sets the 'searchTerm' state - used when search executes to filter
   * by keyword
   * @param searchTerm
   */
  setSearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  /**
   * Returns the current search term state
   */
  getSearchTerm(): string {
    return this.searchTerm;
  }

  /**
   * Returns true if a search term is currently held in memory
   */
  hasSearchTerm(): boolean {
    return !!this.searchTerm;
  }

  /**
   * Get the value of the search box, search the table, then update the datasource
   * to reflect data items that been filtered out.
   */
  searchTable(clear = false) {
    const startSearch = performance.now();
    this.setSearchApplied(true);
    if (!this.searchTerm) {
      this.setSearchApplied(false);
    }
    if (clear) {
      this.clearSearchResults();
      return;
    }

    if (this.memoizedSearch[this.searchTerm]) {
      this.applySearchResults(this.memoizedSearch[this.searchTerm]);
    } else {
      this.host._dataRefreshing = true;
      searchDataSource(
        this.host._dataSource,
        this.host._headers,
        this.searchTerm,
        this.host.searchOptions?.searchableColumns,
        this.host.searchOptions?.excludeFiltered,
        this.host.searchOptions?.deepSearch
      ).then((searchResults) => {
        this.applySearchResults(searchResults);
        this.memoizedSearch[this.searchTerm] = searchResults;
        Utils.logPerformance('searchTable', startSearch);
      });
    }
  }

  /**
   * Clear search results by resetting the SEARCHED metadata on each
   * data item to false
   */
  clearSearchResults() {
    const startClearSearch = performance.now();
    this.host._dataSource = this.host._dataSource.map((ds) => {
      const updatedDs = {
        ...ds,
        [VISIBLE]: true,
        [SEARCHED]: false,
      };
      // don't exclude filtered column data if excludeFiltered is false
      if (!this.host.searchOptions?.excludeFiltered) {
        updatedDs[FILTERED] = false;
      }
      return updatedDs;
    });
    this.host.groupAndPaginate();
    Utils.logPerformance('clearSearchResults', startClearSearch);
  }

  /**
   * Given a set of search results, apply those to the data source.
   * @param searchResults
   */
  applySearchResults(searchResults: FluidRowData[]) {
    this.emitSearchEvent({
      searchTerm: this.searchTerm,
      searchResults,
    });
    const startApplySearchResults = performance.now();
    const searchResultsLookup = new Set(searchResults.map((sr) => sr[ID]));
    this.searchHasResults = searchResultsLookup.size > 0;
    this.host._dataSource = this.host._dataSource.map((ds) => {
      const isInSearchResults = searchResultsLookup.has(ds[ID]);
      const updatedDs = {
        ...ds,
        [VISIBLE]: isInSearchResults,
        [SEARCHED]: !isInSearchResults,
      };
      // don't exclude filtered column data if excludeFiltered is false
      if (!this.host.searchOptions?.excludeFiltered) {
        updatedDs[FILTERED] = !isInSearchResults;
      }

      return updatedDs;
    });
    Utils.logPerformance('applySearchResults', startApplySearchResults);
    this.host.groupAndPaginate();
  }

  /**
   * Emit the search result payload from the tableSearched emitter
   * and propagate event to data layer.
   * @param searchResultPayload
   */
  emitSearchEvent(searchResultPayload: {
    searchTerm: string;
    searchResults: FluidRowData[];
  }) {
    if (this.host._lastSearchedTerm !== searchResultPayload.searchTerm) {
      this.host.tableSearched.emit(searchResultPayload);
      sendEvent(
        this.host,
        this.host.parentLayerId,
        'tableSearched',
        searchResultPayload
      );
      this.host._lastSearchedTerm = searchResultPayload.searchTerm;
    }
  }

  /**
   * Handle the table search. If search is dynamic then debounce search, so we
   * don't fire so often and cause render jitter.
   * @param searchTerm
   */
  async handleSearchTermUpdate(searchTerm: string) {
    const clearSearch = async () => {
      this.searchTerm = '';
      if (this.host.searchOptions?.retainFiltersOnClear) {
        this.searchTable(true);
        await processTableFilters(this.host);
      } else {
        this.host.clearFilters().then(() => this.searchTable(true));
      }
    };
    const debouncedSearch = Utils.debounce(
      () => this.searchTable(),
      this.host.searchOptions?.debounceTime
    );
    this.searchTerm = searchTerm;
    if (!this.searchTerm) {
      await clearSearch();
    }
    if (this.host.searchOptions?.dynamicSearch) {
      debouncedSearch();
    }
  }
}
